import { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download, ArrowDown } from "lucide-react";

const DISMISS_KEY = "refugium_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isSafari() {
  const ua = window.navigator.userAgent || "";
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPWAPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Android / Chrome / Edge: capture beforeinstallprompt
    const handleBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handleBIP);

    // iOS Safari: no beforeinstallprompt — show manual instructions modal.
    // Only show on Safari (Chrome/Firefox on iOS can't install PWAs).
    if (isIOS() && isSafari()) {
      setIos(true);
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        window.removeEventListener("beforeinstallprompt", handleBIP);
        clearTimeout(t);
      };
    }

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBIP);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  // iOS: full-screen modal with big illustration + steps
  if (ios) {
    return (
      <div
        data-testid="install-pwa-prompt"
        className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={dismiss}
      >
        <div
          className="relative w-full sm:max-w-md mx-auto rounded-t-3xl sm:rounded-3xl bg-[#241812] text-[#F5EEDB] shadow-2xl border-t sm:border border-[#3a2a1f] animate-in slide-in-from-bottom duration-500 pb-[max(env(safe-area-inset-bottom),1.25rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            data-testid="install-pwa-dismiss"
            onClick={dismiss}
            aria-label="Cerrar"
            className="absolute top-4 right-4 text-[#d8c9a9] hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="px-6 pt-8 pb-2 text-center">
            <img
              src="/logo192.png"
              alt="Refugium"
              className="w-24 h-24 mx-auto rounded-2xl shadow-lg mb-4"
            />
            <h2 className="text-2xl font-heading font-semibold text-[#F5EEDB] mb-2">
              Instala Refugium en tu iPhone
            </h2>
            <p className="text-sm text-[#d8c9a9] leading-relaxed">
              Acceso rápido desde tu pantalla de inicio, como una app.
            </p>
          </div>

          <div className="px-6 pt-6 pb-4 space-y-4">
            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E07A3F] text-white text-sm font-bold flex items-center justify-center">
                1
              </span>
              <div className="flex-1 pt-0.5">
                <p className="text-sm text-[#F5EEDB] leading-relaxed">
                  Pulsa el botón{" "}
                  <span className="inline-flex items-center justify-center w-7 h-7 mx-1 -mt-0.5 align-middle rounded-md bg-[#3a2a1f] border border-[#4a3a2f]">
                    <Share className="w-4 h-4 text-[#F5EEDB]" />
                  </span>{" "}
                  <strong>Compartir</strong> en la barra de Safari.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E07A3F] text-white text-sm font-bold flex items-center justify-center">
                2
              </span>
              <div className="flex-1 pt-0.5">
                <p className="text-sm text-[#F5EEDB] leading-relaxed">
                  Desplázate y selecciona{" "}
                  <span className="inline-flex items-center gap-1 mx-1 align-middle">
                    <strong>"Añadir a pantalla de inicio"</strong>
                    <PlusSquare className="w-4 h-4 text-[#F5EEDB]" />
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E07A3F] text-white text-sm font-bold flex items-center justify-center">
                3
              </span>
              <div className="flex-1 pt-0.5">
                <p className="text-sm text-[#F5EEDB] leading-relaxed">
                  Confirma <strong>"Añadir"</strong> arriba a la derecha. ¡Listo!
                </p>
              </div>
            </div>
          </div>

          {/* Arrow pointing down to Safari share button */}
          <div className="flex flex-col items-center pt-2 pb-1">
            <p className="text-xs text-[#d8c9a9] mb-1">
              El botón <Share className="inline w-3.5 h-3.5 mx-0.5" /> está abajo
            </p>
            <ArrowDown className="w-6 h-6 text-[#E07A3F] animate-bounce" />
          </div>

          <div className="px-6 pt-3">
            <button
              data-testid="install-pwa-later"
              onClick={dismiss}
              className="w-full rounded-full border border-[#4a3a2f] text-[#d8c9a9] hover:text-white hover:bg-white/5 text-sm font-medium py-2.5 transition-colors"
            >
              Más tarde
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android / desktop: bottom banner with native install CTA
  return (
    <div
      data-testid="install-pwa-prompt"
      className="fixed inset-x-0 bottom-0 z-[9999] px-4 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 pointer-events-none"
    >
      <div className="mx-auto max-w-md rounded-2xl border border-[#3a2a1f] bg-[#241812] text-[#F5EEDB] shadow-2xl pointer-events-auto">
        <div className="flex items-start gap-3 p-4">
          <img
            src="/logo192.png"
            alt="Refugium"
            className="w-12 h-12 rounded-xl flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#F5EEDB]">
              Instala Refugium en tu móvil
            </p>
            <p className="text-xs text-[#d8c9a9] mt-1 leading-relaxed">
              Accede más rápido y como una app, con tu logo en la pantalla de inicio.
            </p>
          </div>
          <button
            data-testid="install-pwa-dismiss"
            onClick={dismiss}
            aria-label="Cerrar"
            className="text-[#d8c9a9] hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-4">
          <button
            data-testid="install-pwa-cta"
            onClick={install}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E07A3F] hover:bg-[#c96a34] text-white text-sm font-semibold py-2.5 transition-colors"
          >
            <Download className="w-4 h-4" />
            Instalar app
          </button>
        </div>
      </div>
    </div>
  );
}
